import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function NotFoundPage({
  authenticated = true,
  fullscreen = true,
  title,
  description,
  homeHref = '/',
  homeLabel,
}: Readonly<{
  authenticated?: boolean;
  fullscreen?: boolean;
  title?: string;
  description?: string;
  homeHref?: string;
  homeLabel?: string;
}>) {
  const { t } = useTranslation('common');
  const resolvedTitle = title ?? t('notFoundPage.title');
  const resolvedDescription = description ?? t('notFoundPage.description');
  const resolvedHomeLabel = homeLabel ?? t('notFoundPage.home');

  return (
    <div
      className={cn(
        'bg-gradient-page-auth w-full flex flex-col items-center justify-center overflow-hidden px-6',
        fullscreen ? 'h-svh' : 'min-h-105 h-full rounded-2xl py-12',
      )}
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-8 max-w-lg text-center">
        <div className="flex flex-col items-center gap-6">
          <img
            src="/assets/welya-splash.png"
            alt="Welya"
            className="w-32 h-32 object-contain opacity-90"
            draggable={false}
          />
          <p className="text-sm font-medium tracking-widest text-white/50">404</p>
        </div>

        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-white">{resolvedTitle}</h2>
          <p className="text-sm text-white/70">{resolvedDescription}</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-center">
          {authenticated && (
            <Link
              to={homeHref}
              className="inline-flex items-center gap-2 bg-button-primary hover:bg-button-primary/90 text-dark font-semibold px-8 py-3 rounded-full shadow-lg transition-colors"
            >
              <Home className="size-4" />
              {resolvedHomeLabel}
            </Link>
          )}
          <a
            href="mailto:support@welya.io"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white font-medium px-8 py-3 rounded-full transition-colors"
          >
            <Mail className="size-4" />
            {t('notFoundPage.contactSupport')}
          </a>
        </div>
      </div>
    </div>
  );
}
